// @erp/module-cms/schemas — the CROSS-CUTTING "private records" wire contracts,
// SHARED by the tenders / events / publications content types (the slice their 0005/
// 0006/0007 migrations each DEFERRED). Browser-safe: zod + plain data only (the admin
// form and the gateway parse the SAME schemas). Feature-referenced from MBSPSU (the
// prospectus private_pin) — rebuilt for the target stack.
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ A record is PUBLIC (default) or PRIVATE. A PRIVATE published record shows only  │
// │ LOCKED metadata on the anonymous public read-split (title/slug/dates/summary +  │
// │ access_level + is_locked); its body + downloadable assets are WITHHELD until a  │
// │ visitor UNLOCKS it with a PIN (POST …/:slug/unlock).                            │
// │                                                                                 │
// │ SECURITY — `pin` is WRITE-ONLY: it rides Create/Update, is hashed server-side   │
// │ (base64(HMAC-SHA256(CMS_PIN_SECRET, pin)) — pin.ts), and is NEVER returned on    │
// │ ANY DTO. The read shapes expose access_level + a DERIVED is_locked boolean only  │
// │ — never `pin` and never `pin_hash`. min 6 chars keeps the PIN out of the         │
// │ trivially-brute-forceable range (the in-scope defense; a distributed unlock      │
// │ rate-limit belongs in a Durable Object — a documented followup, service.ts).     │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";

// ── The access-level vocabulary — public | private. DISTINCT from the gallery's
// GalleryAccessLevel (public|internal|restricted): the gallery hint gates a whole
// item's public visibility, whereas this two-value dimension gates the body/assets of
// an otherwise-published tender/event/publication behind a PIN. ────────────────────────
export const RECORD_ACCESS_LEVELS = ["public", "private"] as const;
export const RecordAccessLevel = z.enum(RECORD_ACCESS_LEVELS);
export type RecordAccessLevel = z.infer<typeof RecordAccessLevel>;

/** The PIN itself — WRITE-ONLY (Create/Update only), min 6 chars, never surfaced on a DTO. */
export const PIN_MIN = 6;
export const PIN_MAX = 128;
export const PinField = z.string().min(PIN_MIN).max(PIN_MAX);

/**
 * The anonymous unlock body — POST /public/{tenders,events,publications}/:slug/unlock.
 * A single `pin`. Every unlock FAILURE (unknown slug, not-private, wrong PIN) answers
 * an IDENTICAL 401 (anti-enumeration — the router/service never reveal which), so the
 * only shape validated here is the PIN's presence + min length.
 */
export const UnlockBody = z.object({
  pin: PinField,
});
export type UnlockBody = z.infer<typeof UnlockBody>;
